import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Chip,
    TextField,
    InputAdornment,
    Collapse,
    Link,
    Menu,
    MenuItem,
    useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { alpha, keyframes, useTheme } from '@mui/material/styles';

import {
    BOOKS,
    CATEGORIES,
    CATEGORY_LABELS,
    AUDIENCE_TAGS,
    START_HERE,
    getBookById,
    getReaderFlexOriginals,
    getThirdPartyBooks,
    getAllThirdPartyBooks,
} from '../data/books';
import { getPurchaseUrl, isPurchaseEnabled, getReviewUrl } from '../utils/amazon';
import { trackEvent } from '../utils/analytics';
import BookCover from '../components/books/BookCover';
import FeedbackModal from '../components/books/FeedbackModal';

const cyan = '#00BFFF';
const green = '#8DC63F';
const violet = '#7C6BFF';

const dotTravel = keyframes`
  0% { left: 2%; opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { left: 98%; opacity: 0; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = () => setReduced(mq.matches);
        update();
        mq.addEventListener?.('change', update);
        return () => mq.removeEventListener?.('change', update);
    }, []);
    return reduced;
}

/* ---------------------------------------------------------------------- */
/* Reading-path motif                                                      */
/* ---------------------------------------------------------------------- */
const ReadingPath = ({ reduced }) => (
    <Box
        aria-hidden="true"
        sx={{
            position: 'relative',
            height: 2,
            width: '100%',
            maxWidth: 460,
            borderRadius: 2,
            background: (theme) =>
                `linear-gradient(90deg, transparent, ${alpha(cyan, theme.palette.mode === 'dark' ? 0.35 : 0.25)}, transparent)`,
            my: 3,
        }}
    >
        <Box
            sx={{
                position: 'absolute',
                top: '50%',
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: cyan,
                boxShadow: `0 0 12px 3px ${alpha(cyan, 0.7)}`,
                transform: 'translateY(-50%)',
                left: reduced ? '50%' : '2%',
                animation: reduced ? 'none' : `${dotTravel} 6s ease-in-out infinite`,
            }}
        />
    </Box>
);

/* ---------------------------------------------------------------------- */
/* Shared affiliate disclosure                                             */
/* ---------------------------------------------------------------------- */
const AffiliateDisclosure = ({ compact = false }) => (
    <Box
        sx={{
            borderLeft: `2px solid ${alpha(cyan, 0.5)}`,
            pl: 2,
            py: compact ? 0.5 : 1,
            maxWidth: 620,
        }}
    >
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Some links on this page are affiliate links. ReaderFLEX may earn a commission from qualifying
            purchases at no additional cost to you.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, color: 'text.primary' }}>
            As an Amazon Associate I earn from qualifying purchases.
        </Typography>
    </Box>
);

/* ---------------------------------------------------------------------- */
/* Status badge helper                                                     */
/* ---------------------------------------------------------------------- */
const StatusChip = ({ book }) => {
    if (book.status === 'coming-soon') {
        return <Chip label="Coming Soon" size="small" sx={{ backgroundColor: alpha(violet, 0.15), color: violet, fontWeight: 600 }} />;
    }
    if (book.editionLabel && book.status === 'published' && book.isReaderFlexTitle) {
        return <Chip label={book.editionLabel} size="small" variant="outlined" />;
    }
    return null;
};

/* ---------------------------------------------------------------------- */
/* Amazon purchase CTA (shared)                                            */
/* ---------------------------------------------------------------------- */
const PurchaseButton = ({ book, position, variant = 'contained', fullWidth = false }) => {
    const enabled = isPurchaseEnabled(book);
    const url = getPurchaseUrl(book);

    const handleClick = () => {
        trackEvent(book.isReaderFlexTitle ? 'readerflex_book_affiliate_click' : 'book_affiliate_click', {
            book_id: book.id,
            book_title: book.title,
            category: book.categories?.[0],
            is_readerflex: book.isReaderFlexTitle,
            featured: Boolean(book.isFeatured),
            position,
        });
    };

    if (book.status === 'coming-soon') {
        return (
            <Button variant="outlined" color="primary" disabled fullWidth={fullWidth} sx={{ fontWeight: 600 }}>
                Coming Soon
            </Button>
        );
    }

    if (!enabled || !url) {
        // No verified ASIN yet: CTA disabled, record needs completion.
        return (
            <Button
                variant="outlined"
                color="primary"
                disabled
                fullWidth={fullWidth}
                sx={{ fontWeight: 600 }}
                title="This title is awaiting a verified Amazon product link."
            >
                Link Coming Soon
            </Button>
        );
    }

    const label = book.isReaderFlexTitle ? 'Get the ReaderFLEX Book' : 'View on Amazon';

    return (
        <Button
            component="a"
            href={url}
            target="_blank"
            rel="sponsored nofollow noopener noreferrer"
            onClick={handleClick}
            variant={variant}
            color={book.isReaderFlexTitle ? 'secondary' : 'primary'}
            fullWidth={fullWidth}
            endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            sx={{ fontWeight: 600 }}
        >
            {label}
        </Button>
    );
};

/* ---------------------------------------------------------------------- */
/* Review + feedback controls (ReaderFLEX titles only)                     */
/* ---------------------------------------------------------------------- */
const REVIEW_FORMAT_LABELS = { ebook: 'eBook', paperback: 'Paperback', hardcover: 'Hardcover' };

const ReaderFlexEngagement = ({ book, onOpenFeedback }) => {
    const [reviewAnchor, setReviewAnchor] = useState(null);
    const reviewFormats = book.reviewFormats || [];
    const hasMultipleFormats = reviewFormats.length > 1;
    const directReviewUrl = getReviewUrl(book);
    const reviewUrl = directReviewUrl || '/review';

    const handleReviewClick = (event) => {
        if (hasMultipleFormats) {
            setReviewAnchor(event.currentTarget);
            return;
        }
        trackEvent('amazon_review_click', {
            book_id: book.id,
            book_title: book.title,
            is_readerflex: true,
        });
    };

    const handleFormatClick = (format) => {
        trackEvent('amazon_review_click', {
            book_id: book.id,
            book_title: book.title,
            is_readerflex: true,
            format,
        });
        setReviewAnchor(null);
    };

    const handleFeedbackOpen = () => {
        trackEvent('private_feedback_open', {
            book_id: book.id,
            book_title: book.title,
        });
        onOpenFeedback(book);
    };

    return (
        <Box sx={{ mt: 2, pt: 2, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Finished the book? Share your experience.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5, lineHeight: 1.6 }}>
                Honest feedback helps other readers decide whether this book is right for them and helps
                ReaderFLEX improve future editions.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                    component={hasMultipleFormats ? 'button' : RouterLink}
                    to={hasMultipleFormats ? undefined : reviewUrl}
                    onClick={handleReviewClick}
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<RateReviewOutlinedIcon />}
                    endIcon={hasMultipleFormats ? <ExpandMoreIcon /> : undefined}
                >
                    Leave an Honest Review on Amazon
                </Button>
                <Menu
                    anchorEl={reviewAnchor}
                    open={Boolean(reviewAnchor)}
                    onClose={() => setReviewAnchor(null)}
                >
                    {reviewFormats.map((format) => (
                        <MenuItem
                            key={format}
                            component={RouterLink}
                            to={`/review/${book.slug}-${format}`}
                            onClick={() => handleFormatClick(format)}
                        >
                            Review {REVIEW_FORMAT_LABELS[format] || format}
                        </MenuItem>
                    ))}
                </Menu>
                <Button
                    onClick={handleFeedbackOpen}
                    variant="text"
                    color="primary"
                    size="small"
                    startIcon={<FeedbackOutlinedIcon />}
                >
                    Send Private Feedback
                </Button>
            </Box>
        </Box>
    );
};

/* ---------------------------------------------------------------------- */
/* ReaderFLEX Original card (lead = oversized two-column)                  */
/* ---------------------------------------------------------------------- */
const OriginalCard = ({ book, lead, position, onOpenFeedback }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                position: 'relative',
                height: '100%',
                borderRadius: 4,
                p: { xs: 2.5, md: lead ? 4 : 3 },
                background: isDark
                    ? `linear-gradient(150deg, ${alpha('#10203A', 0.9)} 0%, ${alpha('#0B1628', 0.95)} 100%)`
                    : '#FFFFFF',
                border: `1px solid ${alpha(cyan, isDark ? 0.35 : 0.3)}`,
                boxShadow: isDark
                    ? `0 16px 50px ${alpha('#000', 0.5)}, 0 0 0 1px ${alpha(cyan, 0.08)}`
                    : `0 8px 30px ${alpha('#000', 0.08)}`,
                overflow: 'hidden',
                animation: `${fadeUp} 0.5s ease both`,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: `linear-gradient(90deg, ${cyan}, ${alpha(violet, 0.8)})`,
                },
            }}
        >
            <Grid container spacing={lead ? 4 : 3} alignItems="flex-start">
                <Grid item xs={12} sm={lead ? 4 : 4} md={lead ? 4 : 4}>
                    <BookCover
                        src={book.coverSrc}
                        alt={book.coverAlt}
                        title={book.title}
                        author={book.author}
                        featured
                        eager={lead}
                        height={lead ? 340 : 260}
                        fit={book.coverFit || 'cover'}
                    />
                </Grid>
                <Grid item xs={12} sm={lead ? 8 : 8} md={lead ? 8 : 8}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                        <Chip
                            label="ReaderFLEX Original"
                            size="small"
                            sx={{
                                backgroundColor: alpha(cyan, 0.15),
                                color: cyan,
                                fontWeight: 700,
                                border: `1px solid ${alpha(cyan, 0.35)}`,
                            }}
                        />
                        <StatusChip book={book} />
                    </Box>

                    <Typography
                        variant={lead ? 'h4' : 'h5'}
                        component="h3"
                        sx={{ fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.15 }}
                    >
                        {book.title}
                    </Typography>
                    {book.subtitle && (
                        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
                            {book.subtitle}
                        </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {book.author}
                        {book.publisher ? ` \u2022 ${book.publisher}` : ''}
                    </Typography>

                    <Typography variant="body1" sx={{ mt: 2, lineHeight: 1.7 }}>
                        {book.description}
                    </Typography>

                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: (t) => alpha(t.palette.mode === 'dark' ? cyan : '#000', 0.05),
                        }}
                    >
                        <Typography variant="body2">
                            <Box component="span" sx={{ fontWeight: 700, color: cyan }}>
                                Best for:{' '}
                            </Box>
                            {book.bestFor}
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {book.learnMoreUrl && (
                            <Button
                                component={RouterLink}
                                to={book.learnMoreUrl}
                                variant="outlined"
                                color="secondary"
                                onClick={() =>
                                    trackEvent('book_card_open', {
                                        book_id: book.id,
                                        book_title: book.title,
                                        action: 'learn-more',
                                    })
                                }
                                sx={{ fontWeight: 600 }}
                            >
                                Why Should I Get This Book?
                            </Button>
                        )}
                        <PurchaseButton book={book} position={position} />
                        {book.sampleUrl && (
                            <Button
                                component={RouterLink}
                                to={book.sampleUrl}
                                variant="text"
                                color="primary"
                                startIcon={<MenuBookOutlinedIcon />}
                                onClick={() =>
                                    trackEvent('book_card_open', {
                                        book_id: book.id,
                                        book_title: book.title,
                                        action: 'sample',
                                    })
                                }
                            >
                                Read a Sample
                            </Button>
                        )}
                    </Box>

                    <ReaderFlexEngagement book={book} onOpenFeedback={onOpenFeedback} />
                </Grid>
            </Grid>
        </Box>
    );
};

/* ---------------------------------------------------------------------- */
/* General library card (third-party)                                      */
/* ---------------------------------------------------------------------- */
const LibraryCard = ({ book, position }) => {
    const [expanded, setExpanded] = useState(false);

    const toggleWhy = () => {
        const next = !expanded;
        setExpanded(next);
        if (next) {
            trackEvent('book_card_open', {
                book_id: book.id,
                book_title: book.title,
                action: 'why_selected',
            });
        }
    };

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                p: 2.5,
                backgroundColor: 'background.paper',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                transition: 'border-color 0.2s ease, transform 0.2s ease',
                '&:hover': { borderColor: alpha(cyan, 0.3), transform: 'translateY(-2px)' },
            }}
        >
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ width: 88, flexShrink: 0 }}>
                    <BookCover src={book.coverSrc} alt={book.coverAlt} title={book.title} author={book.author} height={132} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                        {book.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {book.author}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                            label={CATEGORY_LABELS[book.categories?.[0]] || 'Reading'}
                            size="small"
                            sx={{ backgroundColor: alpha(cyan, 0.12), color: cyan, fontWeight: 600 }}
                        />
                        {(book.badges || []).slice(0, 2).map((b) => (
                            <Chip key={b} label={b} size="small" variant="outlined" />
                        ))}
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    mt: 1.5,
                    p: 1.25,
                    borderRadius: 2,
                    backgroundColor: (t) => alpha(t.palette.mode === 'dark' ? cyan : '#000', 0.04),
                }}
            >
                <Typography variant="body2">
                    <Box component="span" sx={{ fontWeight: 700, color: cyan }}>
                        Best for:{' '}
                    </Box>
                    {book.bestFor}
                </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.65, flexGrow: 1 }}>
                {book.description}
            </Typography>

            {book.readerflexNote && (
                <Box sx={{ mt: 1 }}>
                    <Button
                        onClick={toggleWhy}
                        size="small"
                        color="primary"
                        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        aria-expanded={expanded}
                        sx={{ px: 0, textTransform: 'none' }}
                    >
                        Why ReaderFLEX selected it
                    </Button>
                    <Collapse in={expanded}>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                            {book.readerflexNote}
                        </Typography>
                    </Collapse>
                </Box>
            )}

            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <PurchaseButton book={book} position={position} variant="outlined" />
                {book.relatedArticleUrl && (
                    <Button component={RouterLink} to={book.relatedArticleUrl} size="small" color="primary" variant="text">
                        Related ReaderFLEX article
                    </Button>
                )}
            </Box>
        </Box>
    );
};

/* ---------------------------------------------------------------------- */
/* Start Here pathway                                                      */
/* ---------------------------------------------------------------------- */
const StartHereSection = () => (
    <Box sx={{ py: { xs: 6, md: 9 } }}>
        <Container maxWidth="md">
            <Typography variant="h3" component="h2" align="center" sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', md: '2.2rem' } }}>
                Start Here
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ fontWeight: 400, mt: 1.5, mb: 5, maxWidth: 640, mx: 'auto' }}>
                Six books that cover the complete journey from reading efficiently to remembering and using what
                you learn.
            </Typography>

            <Box sx={{ position: 'relative' }}>
                {START_HERE.map((entry, i) => {
                    const book = getBookById(entry.bookId);
                    if (!book) return null;
                    return (
                        <Box
                            key={entry.bookId}
                            sx={{ display: 'flex', gap: { xs: 2, md: 3 }, mb: 4, alignItems: 'flex-start' }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        border: `2px solid ${alpha(cyan, 0.4)}`,
                                        backgroundColor: alpha(cyan, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: cyan,
                                        fontWeight: 800,
                                    }}
                                >
                                    {i + 1}
                                </Box>
                                {i < START_HERE.length - 1 && (
                                    <Box sx={{ width: 2, flexGrow: 1, minHeight: 40, mt: 1, backgroundColor: alpha(cyan, 0.15) }} />
                                )}
                            </Box>
                            <Box sx={{ pb: 1 }}>
                                <Typography variant="overline" sx={{ color: cyan, letterSpacing: '0.08em' }}>
                                    {entry.role}
                                </Typography>
                                <Typography variant="h6" component="h3" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                                    {book.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {book.author}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.65 }}>
                                    {entry.blurb}
                                </Typography>
                                <Box sx={{ mt: 1.5 }}>
                                    <PurchaseButton book={book} position={`start-here-${i + 1}`} variant="outlined" />
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Container>
    </Box>
);

/* ---------------------------------------------------------------------- */
/* Main page                                                               */
/* ---------------------------------------------------------------------- */
const Books = () => {
    const theme = useTheme();
    const reduced = usePrefersReducedMotion();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchParams, setSearchParams] = useSearchParams();

    const [feedbackBook, setFeedbackBook] = useState(null);

    const query = searchParams.get('q') || '';
    const category = searchParams.get('category') || 'all';
    const audience = searchParams.get('audience') || 'all';

    const [searchInput, setSearchInput] = useState(query);

    // Fire the page-view event once.
    useEffect(() => {
        trackEvent('book_library_view', { total_books: BOOKS.length });
    }, []);

    // Debounce search input into the URL query param.
    useEffect(() => {
        const handle = setTimeout(() => {
            if (searchInput === query) return;
            const next = new URLSearchParams(searchParams);
            if (searchInput) {
                next.set('q', searchInput);
                trackEvent('book_search', { query_length: searchInput.length });
            } else {
                next.delete('q');
            }
            setSearchParams(next, { replace: true });
        }, 350);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    const updateParam = useCallback(
        (key, value) => {
            const next = new URLSearchParams(searchParams);
            if (value && value !== 'all') next.set(key, value);
            else next.delete(key);
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    const handleCategory = (id) => {
        updateParam('category', id);
        trackEvent('book_category_filter', { category: id });
    };

    const handleAudience = (tag) => {
        updateParam('audience', audience === tag ? 'all' : tag);
    };

    const clearAll = () => {
        setSearchInput('');
        setSearchParams(new URLSearchParams(), { replace: true });
    };

    const matchesFilters = useCallback(
        (book) => {
            if (category !== 'all' && !book.categories.includes(category)) return false;
            if (audience !== 'all' && !(book.audienceTags || []).includes(audience)) return false;
            if (query) {
                const haystack = [
                    book.title,
                    book.author,
                    book.description,
                    book.bestFor,
                    ...(book.categories || []).map((c) => CATEGORY_LABELS[c] || ''),
                    ...(book.audienceTags || []),
                    ...(book.badges || []),
                ]
                    .join(' ')
                    .toLowerCase();
                if (!haystack.includes(query.toLowerCase())) return false;
            }
            return true;
        },
        [category, audience, query]
    );

    const originals = useMemo(() => getReaderFlexOriginals(), []);
    const filteredOriginals = useMemo(() => originals.filter(matchesFilters), [originals, matchesFilters]);
    const thirdParty = useMemo(() => getThirdPartyBooks(), []);
    const filteredLibrary = useMemo(() => thirdParty.filter(matchesFilters), [thirdParty, matchesFilters]);
    const hasVisibleThirdParty = thirdParty.length > 0;

    const filtersActive = Boolean(query) || category !== 'all' || audience !== 'all';
    const resultCount = filteredLibrary.length + filteredOriginals.length;

    const leadOriginal = filteredOriginals[0];
    const restOriginals = filteredOriginals.slice(1);

    // ---- Structured data ----
    const canonical = 'https://readerflex.com/books';
    const itemListJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'The ReaderFLEX Book Library',
        itemListElement: BOOKS.map((b, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: {
                '@type': 'Book',
                name: b.title,
                author: { '@type': 'Person', name: b.author },
                ...(b.publisher ? { publisher: { '@type': 'Organization', name: b.publisher } } : {}),
            },
        })),
    };
    const breadcrumbJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://readerflex.com/' },
            { '@type': 'ListItem', position: 2, name: 'Book Library', item: canonical },
        ],
    };

    const openFeedback = (book) => setFeedbackBook(book);

    return (
        <>
            <Helmet>
                <title>Best Books for Speed Reading, Focus, Memory and Learning | ReaderFLEX</title>
                <meta
                    name="description"
                    content="Explore ReaderFLEX's curated library of books on speed reading, comprehension, focus, memory, learning, note-taking, and the science of reading."
                />
                <link rel="canonical" href={canonical} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonical} />
                <meta property="og:title" content="The ReaderFLEX Book Library" />
                <meta
                    property="og:description"
                    content="A curated library of books on speed reading, comprehension, focus, memory, learning, note-taking, and reading science."
                />
                <meta property="og:site_name" content="ReaderFLEX" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="The ReaderFLEX Book Library" />
                <meta
                    name="twitter:description"
                    content="Books to help you read faster, focus longer, understand more, and remember what matters."
                />
                <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
                <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
            </Helmet>

            <Box>
                {/* 1. HERO */}
                <Box
                    sx={{
                        position: 'relative',
                        pt: { xs: 6, md: 10 },
                        pb: { xs: 5, md: 7 },
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            background: (t) =>
                                t.palette.mode === 'dark'
                                    ? `radial-gradient(ellipse at 30% 0%, ${alpha(cyan, 0.08)} 0%, transparent 55%), radial-gradient(ellipse at 80% 40%, ${alpha(violet, 0.06)} 0%, transparent 50%)`
                                    : 'none',
                            pointerEvents: 'none',
                        },
                    }}
                >
                    <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                        <Typography
                            variant="h1"
                            component="h1"
                            sx={{ fontWeight: 800, fontSize: { xs: '2rem', sm: '2.6rem', md: '3.2rem' }, letterSpacing: '-0.02em' }}
                        >
                            The ReaderFLEX Book Library
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mt: 2, maxWidth: 660, mx: 'auto', lineHeight: 1.6 }}>
                            Books to help you read faster, focus longer, understand more, and remember what matters.
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <ReadingPath reduced={reduced} />
                        </Box>

                        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, mx: 'auto', lineHeight: 1.8 }}>
                            Choosing the right pace, sustaining your attention, understanding what you read, and being able to use it afterward. This library brings together practical guides, research-grounded books, and ReaderFLEX resources to support every part of that process.
                        </Typography>

                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                            {hasVisibleThirdParty && <AffiliateDisclosure />}
                        </Box>
                    </Container>
                </Box>

                {/* 2. READERFLEX ORIGINALS */}
                {filteredOriginals.length > 0 && (
                    <Box
                        sx={{
                            py: { xs: 6, md: 9 },
                            backgroundColor: (t) => alpha(t.palette.mode === 'dark' ? '#000' : '#000', 0.03),
                            borderTop: (t) => `1px solid ${t.palette.divider}`,
                            borderBottom: (t) => `1px solid ${t.palette.divider}`,
                        }}
                    >
                        <Container maxWidth="lg">
                            <Typography variant="h3" component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.7rem', md: '2.3rem' } }}>
                                ReaderFLEX Originals
                            </Typography>
                            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mt: 1.5, mb: 4, maxWidth: 680, lineHeight: 1.6 }}>
                                Practical reading guides created around the ReaderFLEX approach to speed, focus,
                                comprehension, and retention.
                            </Typography>

                            {leadOriginal && (
                                <Box sx={{ mb: restOriginals.length ? 4 : 0 }}>
                                    <OriginalCard book={leadOriginal} lead position="original-lead" onOpenFeedback={openFeedback} />
                                </Box>
                            )}

                            {restOriginals.length > 0 && (
                                <Grid container spacing={3}>
                                    {restOriginals.map((book, i) => (
                                        <Grid item xs={12} md={6} key={book.id}>
                                            <OriginalCard book={book} position={`original-${i + 2}`} onOpenFeedback={openFeedback} />
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Container>
                    </Box>
                )}

                {/* 3. START HERE (only when no filters active and there are visible third-party books) */}
                {!filtersActive && hasVisibleThirdParty && <StartHereSection />}

                {/* 4. SEARCH + FILTERS (sticky) - only when there are visible third-party books */}
                {hasVisibleThirdParty && (
                <Box
                    sx={{
                        position: 'sticky',
                        top: 64,
                        zIndex: 10,
                        py: 2,
                        backgroundColor: (t) => alpha(t.palette.background.default, 0.92),
                        backdropFilter: 'blur(12px)',
                        borderTop: (t) => `1px solid ${t.palette.divider}`,
                        borderBottom: (t) => `1px solid ${t.palette.divider}`,
                    }}
                >
                    <Container maxWidth="lg">
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by title, author, topic, or who it is best for"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="disabled" />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{ 'aria-label': 'Search the book library' }}
                            sx={{ mb: 1.5 }}
                        />

                        <Box
                            role="group"
                            aria-label="Filter by category"
                            sx={{
                                display: 'flex',
                                gap: 1,
                                overflowX: { xs: 'auto', md: 'visible' },
                                flexWrap: { xs: 'nowrap', md: 'wrap' },
                                pb: { xs: 0.5, md: 0 },
                            }}
                        >
                            {CATEGORIES.map((c) => (
                                <Chip
                                    key={c.id}
                                    label={c.label}
                                    onClick={() => handleCategory(c.id)}
                                    color={category === c.id ? 'primary' : 'default'}
                                    variant={category === c.id ? 'filled' : 'outlined'}
                                    sx={{ fontWeight: 600, flexShrink: 0 }}
                                    aria-pressed={category === c.id}
                                />
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                Audience:
                            </Typography>
                            {AUDIENCE_TAGS.map((tag) => (
                                <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    onClick={() => handleAudience(tag)}
                                    color={audience === tag ? 'secondary' : 'default'}
                                    variant={audience === tag ? 'filled' : 'outlined'}
                                    aria-pressed={audience === tag}
                                />
                            ))}
                            <Box sx={{ flexGrow: 1 }} />
                            <Typography variant="body2" color="text.secondary" aria-live="polite">
                                {resultCount} {resultCount === 1 ? 'book' : 'books'}
                            </Typography>
                            {filtersActive && (
                                <Button onClick={clearAll} size="small" color="primary" sx={{ textTransform: 'none' }}>
                                    Clear all
                                </Button>
                            )}
                        </Box>
                    </Container>
                </Box>
                )}

                {/* 5. GENERAL LIBRARY */}
                {hasVisibleThirdParty && (
                <Box sx={{ py: { xs: 5, md: 8 } }}>
                    <Container maxWidth="lg">
                        {!filtersActive && (
                            <Typography variant="h4" component="h2" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                                The Full Library
                            </Typography>
                        )}
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 680 }}>
                            Thoughtfully selected books across every part of better reading. ReaderFLEX writes its own
                            summaries and does not reproduce publisher descriptions.
                        </Typography>

                        {filteredLibrary.length === 0 ? (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    py: 8,
                                    px: 2,
                                    borderRadius: 3,
                                    border: (t) => `1px dashed ${t.palette.divider}`,
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    No books matched those filters.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Try another category or clear your search.
                                </Typography>
                                <Button onClick={clearAll} variant="outlined" color="primary" sx={{ mt: 3 }}>
                                    Clear all filters
                                </Button>
                            </Box>
                        ) : (
                            <Grid container spacing={3}>
                                {filteredLibrary.map((book, i) => (
                                    <Grid item xs={12} sm={6} lg={4} key={book.id}>
                                        <LibraryCard book={book} position={`library-${i + 1}`} />
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        <Box sx={{ mt: 6 }}>
                            <AffiliateDisclosure compact />
                        </Box>
                    </Container>
                </Box>
                )}
            </Box>

            <FeedbackModal open={Boolean(feedbackBook)} onClose={() => setFeedbackBook(null)} book={feedbackBook || {}} />
        </>
    );
};

export default Books;
